import React, { useState, useEffect } from 'react';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';

const AdminSettings = () => {
    const { showToast, showConfirm } = useUI();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');

    // Settings state
    const [settings, setSettings] = useState({
        multi_site_mode: true,
        app_language: '',
        app_currency: '',
        default_unit: '',

        low_stock_threshold: 10,
        continuous_scan: false,
        email_alerts: true,
        weekly_reports: false,
        external_api_opf_enabled: true,
        external_api_off_enabled: true,
        external_api_upcitemdb_enabled: true
    });

    // Nomenclature state
    const [categories, setCategories] = useState([]);
    const [warehouseTypes, setWarehouseTypes] = useState([]);

    // Isolated Forms for Categories
    const [catForm, setCatForm] = useState({ name: '', color: 'blue', icon: 'category' });
    const [catEditing, setCatEditing] = useState(null);

    // Isolated Forms for Types
    const [typeForm, setTypeForm] = useState({ name: '', color: 'amber', icon: 'warehouse' });
    const [typeEditing, setTypeEditing] = useState(null);

    // Fetch data
    useEffect(() => {
        if (!user?.companyId) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const [sRes, cRes, tRes] = await Promise.all([
                    fetch(`/api/settings/${user.companyId}`),
                    fetch(`/api/categories?companyId=${user.companyId}`),
                    fetch(`/api/warehouse-types?companyId=${user.companyId}`)
                ]);

                if (sRes.ok) {
                    const data = await sRes.json();
                    if (Object.keys(data).length > 0) setSettings(prev => ({ ...prev, ...data }));
                }
                if (cRes.ok) setCategories(await cRes.json());
                if (tRes.ok) setWarehouseTypes(await tRes.json());
            } catch (err) {
                showToast("Erreur lors du chargement des données", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user?.companyId, showToast]);

    const handleToggle = (key) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveGeneral = async () => {
        if (!user?.companyId) return;
        setSaving(true);
        try {
            const response = await fetch(`/api/settings/batch/${user.companyId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings })
            });
            if (response.ok) showToast("Paramètres généraux enregistrés", "success");
            else showToast("Erreur lors de l'enregistrement", "error");
        } catch (err) {
            showToast("Erreur serveur", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleNomSubmit = async (e, type) => {
        e.preventDefault();
        const categoryMode = type === 'category';
        const form = categoryMode ? catForm : typeForm;
        const editingProp = categoryMode ? catEditing : typeEditing;

        const endpoint = categoryMode ? '/api/categories' : '/api/warehouse-types';
        const method = editingProp ? 'PUT' : 'POST';
        const url = editingProp ? `${endpoint}/${editingProp.id}` : endpoint;

        try {
            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, companyId: user.companyId })
            });

            if (response.ok) {
                showToast(editingProp ? "Mis à jour" : "Créé", "success");
                if (categoryMode) {
                    setCatEditing(null);
                    setCatForm({ name: '', color: 'blue', icon: 'category' });
                } else {
                    setTypeEditing(null);
                    setTypeForm({ name: '', color: 'amber', icon: 'warehouse' });
                }

                // Refresh specific list
                const refreshRes = await fetch(`${endpoint}?companyId=${user.companyId}`);
                const data = await refreshRes.json();
                if (categoryMode) setCategories(data);
                else setWarehouseTypes(data);
            }
        } catch (err) {
            showToast("Erreur lors de l'opération", "error");
        }
    };

    const deleteNom = async (id, type) => {
        showConfirm({
            title: "Supprimer l'élément ?",
            message: "Cette action supprimera définitivement cette nomenclature. Les entrepôts ou articles liés pourraient être affectés.",
            onConfirm: async () => {
                const endpoint = type === 'category' ? '/api/categories' : '/api/warehouse-types';
                try {
                    const response = await fetch(`${endpoint}/${id}`, { method: 'DELETE' });
                    if (response.ok) {
                        showToast("Supprimé", "success");
                        if (type === 'category') setCategories(categories.filter(c => c.id !== id));
                        else setWarehouseTypes(warehouseTypes.filter(t => t.id !== id));
                    }
                } catch (err) {
                    showToast("Erreur suppression", "error");
                }
            }
        });
    };

    if (loading) {
        return <div className="p-10 text-center animate-pulse text-slate-500">Chargement de la console d'administration...</div>;
    }

    return (
        <>
            {/* Header Section - Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div>
                    <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Paramètres Robots</h1>
                    <p className="text-slate-500 dark:text-slate-400">Pilotage centralisé de votre infrastructure logistique</p>
                </div>
                {activeTab === 'general' && (
                    <div className="flex items-center gap-3">
                        <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Réinitialiser</button>
                        <button onClick={handleSaveGeneral} disabled={saving} className="px-6 py-3 rounded-xl font-bold bg-primary text-white shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all flex items-center gap-2">
                            {saving ? <span className="animate-spin size-4 border-2 border-white/20 border-b-white rounded-full"></span> : <span className="material-symbols-outlined text-sm font-bold">save</span>}
                            <span>Enregistrer</span>
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs Navigation */}
            <div className="flex gap-8 mb-8 border-b border-slate-100 dark:border-slate-800">
                <button onClick={() => setActiveTab('general')} className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'general' ? 'text-primary' : 'text-slate-400 hover:text-slate-600'}`}>
                    Configuration Générale
                    {activeTab === 'general' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full"></div>}
                </button>
                <button onClick={() => setActiveTab('nomenclatures')} className={`pb-4 text-sm font-bold transition-all relative ${activeTab === 'nomenclatures' ? 'text-primary' : 'text-slate-400 hover:text-slate-600'}`}>
                    Nomenclatures (Bases)
                    {activeTab === 'nomenclatures' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full"></div>}
                </button>
            </div>

            {activeTab === 'general' ? (
                <div className="space-y-12">
                    {/* General Settings Content */}
                    <section>
                        <div className="flex items-center gap-2 mb-6 pb-2 border-b border-slate-200 dark:border-slate-800">
                            <span className="material-symbols-outlined text-primary text-xl">tune</span>
                            <h2 className="text-xl font-bold uppercase tracking-wide text-[10px] text-slate-400">Configuration Système</h2>
                        </div>
                        <div className="grid gap-6">
                            <SettingToggle title="Mode Multi-sites" description="Active ou désactive la gestion et la vue de plusieurs entrepôts." checked={settings.multi_site_mode} onChange={() => handleToggle('multi_site_mode')} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">Langue</label>
                                    <select required className={`bg-white dark:bg-slate-800 border-2 text-sm rounded-lg p-2.5 outline-none ${!settings.app_language ? 'border-amber-500/50' : 'border-slate-200 dark:border-slate-700'}`} value={settings.app_language} onChange={(e) => handleChange('app_language', e.target.value)}>
                                        <option value="">-- Choisir --</option>
                                        <option>Français</option><option>English</option>
                                    </select>

                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">Devise</label>
                                    <select required className={`bg-white dark:bg-slate-800 border-2 text-sm rounded-lg p-2.5 outline-none ${!settings.app_currency ? 'border-amber-500/50' : 'border-slate-200 dark:border-slate-700'}`} value={settings.app_currency} onChange={(e) => handleChange('app_currency', e.target.value)}>
                                        <option value="">-- Choisir --</option>
                                        <option>Euro (€)</option><option>Dollar US ($)</option>
                                    </select>

                                </div>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-6 pb-2 border-b border-slate-200 dark:border-slate-800">
                            <span className="material-symbols-outlined text-primary text-xl">notifications_active</span>
                            <h2 className="text-xl font-bold uppercase tracking-wide text-[10px] text-slate-400">Alertes & Rapports</h2>
                        </div>
                        <div className="grid gap-4">
                            <SettingToggle icon="mail" title="Alertes email Stock Bas" description="Notification immédiate sous le seuil critique." checked={settings.email_alerts} onChange={() => handleToggle('email_alerts')} />
                            <SettingToggle icon="calendar_month" title="Rapport Hebdomadaire" description="Résumé de l'inventaire chaque lundi matin." checked={settings.weekly_reports} onChange={() => handleToggle('weekly_reports')} />
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center gap-2 mb-6 pb-2 border-b border-slate-200 dark:border-slate-800">
                            <span className="material-symbols-outlined text-primary text-xl">search_insights</span>
                            <h2 className="text-xl font-bold uppercase tracking-wide text-[10px] text-slate-400">Recherche Externe (Scanner Mobile)</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <SettingToggle icon="inventory" title="Open Product Facts" description="Informatique, Bureau, Matériel..." checked={settings.external_api_opf_enabled} onChange={() => handleToggle('external_api_opf_enabled')} />
                            <SettingToggle icon="restaurant" title="Open Food Facts" description="Alimentaire, Boissons, Épicerie..." checked={settings.external_api_off_enabled} onChange={() => handleToggle('external_api_off_enabled')} />
                            <SettingToggle icon="database" title="UPCitemdb (Trial)" description="Source de secours (100 req/j)" checked={settings.external_api_upcitemdb_enabled} onChange={() => handleToggle('external_api_upcitemdb_enabled')} />
                        </div>
                    </section>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl">
                    {/* Management Categories */}
                    <NomenclatureSection
                        title="Catégories d'Articles"
                        description="Définissez les catégories partagées pour tout l'inventaire."
                        items={categories}
                        type="category"
                        onEdit={(item) => { setCatEditing(item); setCatForm({ name: item.name, color: item.color, icon: item.icon }); }}
                        onDelete={(id) => deleteNom(id, 'category')}
                        form={catForm}
                        setForm={setCatForm}
                        onSubmit={(e) => handleNomSubmit(e, 'category')}
                        isEditing={catEditing}
                        onCancel={() => { setCatEditing(null); setCatForm({ name: '', color: 'blue', icon: 'category' }); }}
                    />

                    {/* Management Warehouse Types */}
                    <NomenclatureSection
                        title="Types d'Entreposage"
                        description="Types d'environnements logistiques (froid, dangereux, etc.)"
                        items={warehouseTypes}
                        type="warehouseType"
                        onEdit={(item) => { setTypeEditing(item); setTypeForm({ name: item.name, color: item.color, icon: item.icon }); }}
                        onDelete={(id) => deleteNom(id, 'warehouseType')}
                        form={typeForm}
                        setForm={setTypeForm}
                        onSubmit={(e) => handleNomSubmit(e, 'warehouseType')}
                        isEditing={typeEditing}
                        onCancel={() => { setTypeEditing(null); setTypeForm({ name: '', color: 'amber', icon: 'warehouse' }); }}
                    />
                </div>
            )}
        </>
    );
};

const COMMON_ICONS = [
    'category', 'inventory_2', 'package_2', 'devices', 'fastfood', 'stethoscope', 'style',
    'precision_manufacturing', 'local_shipping', 'construction', 'bolt', 'checkroom',
    'warehouse', 'ac_unit', 'warning', 'factory', 'store', 'solar_power', 'home', 'shelves',
    'shopping_cart', 'pets', 'science', 'water_drop', 'restaurant', 'coffee', 'spa',
    'diamond', 'auto_awesome', 'workspaces', 'hub', 'token', 'deployed_code'
];

const NomenclatureSection = ({ title, description, items, type, onEdit, onDelete, form, setForm, onSubmit, isEditing, onCancel }) => {
    const [iconOpen, setIconOpen] = useState(false);

    return (
        <div className="space-y-6">
            <div className="px-2">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white leading-tight">{title}</h2>
                <p className="text-sm text-slate-500 font-medium">{description}</p>
            </div>

            <form onSubmit={onSubmit} className="p-6 bg-white dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-6 shadow-xl shadow-slate-200/20 dark:shadow-none relative overflow-hidden">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{isEditing ? 'Modifier l\'élément' : 'Ajouter une ressource'}</h3>

                <div className="space-y-5">
                    {/* Name */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Désignation</label>
                        <input required placeholder="Nom de la nomenclature" className="w-full p-4 bg-slate-50 dark:bg-slate-800 border-none rounded-xl text-sm font-bold outline-none ring-1 ring-slate-200 dark:ring-slate-700 focus:ring-2 focus:ring-primary transition-all" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>

                    {/* Color + Icon row */}
                    <div className="flex items-end gap-4">
                        {/* Native Color Picker */}
                        <div className="space-y-2 flex-1">
                            <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Couleur</label>
                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700">
                                <input
                                    type="color"
                                    value={form.color}
                                    onChange={e => setForm({ ...form, color: e.target.value })}
                                    className="size-10 rounded-lg cursor-pointer border-none bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-2 [&::-webkit-color-swatch]:border-white"
                                />
                                <span className="text-xs font-mono font-bold text-slate-500 uppercase">{form.color}</span>
                            </div>
                        </div>

                        {/* Icon Toggle Button */}
                        <div className="space-y-2 flex-1">
                            <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Icône</label>
                            <button
                                type="button"
                                onClick={() => setIconOpen(!iconOpen)}
                                className={`w-full flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl ring-1 transition-all ${iconOpen ? 'ring-primary ring-2' : 'ring-slate-200 dark:ring-slate-700 hover:ring-primary/50'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="size-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: form.color + '20' }}>
                                        <span className="material-symbols-outlined text-2xl" style={{ color: form.color }}>{form.icon}</span>
                                    </div>
                                    <span className="text-xs font-mono font-bold text-slate-500">{form.icon}</span>
                                </div>
                                <span className={`material-symbols-outlined text-slate-400 text-sm transition-transform ${iconOpen ? 'rotate-180' : ''}`}>expand_more</span>
                            </button>
                        </div>
                    </div>

                    {/* Collapsible Icon Grid */}
                    {iconOpen && (
                        <div className="grid grid-cols-7 gap-2 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700/50 animate-fade-in">
                            {COMMON_ICONS.map(icon => (
                                <button
                                    key={icon}
                                    type="button"
                                    onClick={() => { setForm({ ...form, icon }); setIconOpen(false); }}
                                    className={`p-2.5 rounded-lg flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 transition-all ${form.icon === icon ? 'bg-white dark:bg-slate-700 shadow-sm ring-2 ring-primary/30' : 'text-slate-400'}`}
                                >
                                    <span className="material-symbols-outlined text-xl" style={form.icon === icon ? { color: form.color } : {}}>{icon}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 pt-2">
                    <button type="submit" className="flex-1 py-4 bg-primary text-white rounded-xl font-black text-sm hover:bg-primary/95 transition-all shadow-lg shadow-primary/30 uppercase tracking-widest">{isEditing ? 'Mettre à jour' : 'Enregistrer'}</button>
                    {isEditing && <button type="button" onClick={onCancel} className="px-6 py-4 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all uppercase tracking-widest">Annuler</button>}
                </div>
            </form>

            <div className="space-y-3">
                {items.map(item => (
                    <div key={item.id} className="group flex items-center justify-between p-4 bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl hover:border-primary/40 hover:shadow-xl hover:shadow-slate-200/40 dark:hover:shadow-none transition-all">
                        <div className="flex items-center gap-5">
                            <div className="size-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110" style={{ backgroundColor: item.color + '20' }}>
                                <span className="material-symbols-outlined text-2xl" style={{ color: item.color }}>{item.icon}</span>
                            </div>
                            <div>
                                <span className="font-black text-slate-800 dark:text-slate-200 block">{item.name}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <div className="size-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.icon}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 mr-2 opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                            <button onClick={() => onEdit(item)} className="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-slate-400 hover:text-primary transition-all">
                                <span className="material-symbols-outlined text-lg">edit</span>
                            </button>
                            <button onClick={() => onDelete(item.id)} className="p-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-slate-400 hover:text-red-500 transition-all">
                                <span className="material-symbols-outlined text-lg">delete</span>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SettingToggle = ({ title, description, icon, checked, onChange }) => (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 transition-all hover:border-primary/30 group">
        <div className="flex items-center gap-4">
            {icon && <span className="material-symbols-outlined text-slate-400 group-hover:text-primary transition-colors text-xl">{icon}</span>}
            <div>
                <p className="text-base font-semibold">{title}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{description}</p>
            </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
            <div className="w-11 h-6 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
        </label>
    </div>
);

export default AdminSettings;
